import { useEffect, useRef, type RefObject } from 'react';

const FOCALISABLES =
  'a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select:not([disabled]),textarea:not([disabled]),iframe,[tabindex]:not([tabindex="-1"])';

function visible(element: HTMLElement): boolean {
  if (element.hidden || element.getAttribute('aria-hidden') === 'true') return false;
  const style = getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

function focalisables(conteneur: HTMLElement): HTMLElement[] {
  return [...conteneur.querySelectorAll<HTMLElement>(FOCALISABLES)].filter(visible);
}

/**
 * Comportement d'une fenêtre modale faite à la main (aperçu plein écran) :
 * le focus entre dans la fenêtre à l'ouverture, Tab y reste enfermé, Échap la
 * ferme, et le focus revient à l'élément qui l'a ouverte à la fermeture. Les
 * boîtes de dialogue de `@artosera/ui` (Radix) font déjà tout cela ; ce hook
 * sert aux couches qui ne peuvent pas s'y ranger.
 */
export function usePiegeFocus(
  conteneur: RefObject<HTMLElement | null>,
  actif: boolean,
  onEchap: () => void,
): void {
  const echap = useRef(onEchap);
  useEffect(() => {
    echap.current = onEchap;
  });

  useEffect(() => {
    if (!actif) return undefined;
    const racine = conteneur.current;
    if (!racine) return undefined;

    const precedent = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Premier contrôle de la fenêtre, à défaut la fenêtre elle-même.
    (focalisables(racine)[0] ?? racine).focus();

    function surTouche(evenement: KeyboardEvent): void {
      if (evenement.key === 'Escape') {
        evenement.preventDefault();
        echap.current();
        return;
      }
      if (evenement.key !== 'Tab' || !racine) return;
      const liste = focalisables(racine);
      const premier = liste[0];
      const dernier = liste[liste.length - 1];
      if (!premier || !dernier) {
        evenement.preventDefault();
        racine.focus();
        return;
      }
      const courant = document.activeElement;
      if (evenement.shiftKey && (courant === premier || !racine.contains(courant))) {
        evenement.preventDefault();
        dernier.focus();
      } else if (!evenement.shiftKey && (courant === dernier || !racine.contains(courant))) {
        evenement.preventDefault();
        premier.focus();
      }
    }

    document.addEventListener('keydown', surTouche);
    return () => {
      document.removeEventListener('keydown', surTouche);
      if (precedent?.isConnected) precedent.focus();
    };
  }, [conteneur, actif]);
}
